import { supabase } from "@/libs/supabase";
import type { User as AuthUser } from "@supabase/supabase-js";
import type { User, DbUser, UserSignupData, SignUpResult } from "@/types/user";

// DbUserからUserへの変換関数
const dbUserToUser = (dbUser: DbUser): User => ({
	id: dbUser.id,
	username: dbUser.username,
	email: dbUser.email,
	fullName: dbUser.full_name || undefined,
	bio: dbUser.bio || undefined,
	avatarUrl: dbUser.avatar_url || undefined,
	createdAt: dbUser.created_at,
	updatedAt: dbUser.updated_at,
	lastLogin: dbUser.last_login || undefined,
	isActive: dbUser.is_active,
	role: dbUser.role,
});

// UserからDbUserへの変換関数
const userToDbUser = (user: User): DbUser => ({
	id: user.id,
	username: user.username,
	email: user.email,
	full_name: user.fullName || null,
	bio: user.bio || null,
	avatar_url: user.avatarUrl || null,
	created_at: user.createdAt,
	updated_at: user.updatedAt,
	last_login: user.lastLogin || null,
	is_active: user.isActive,
	role: user.role,
});

export const signUpUser = async ({
	email,
	password,
	username,
}: UserSignupData): Promise<SignUpResult> => {
	try {
		const { data: authData, error: authError } = await supabase.auth.signUp({
			email,
			password,
		});

		if (authError) throw authError;

		if (authData.user) {
			const newUser: User = {
				id: authData.user.id,
				username,
				email,
				fullName: "",
				bio: "",
				avatarUrl: "",
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
				lastLogin: new Date().toISOString(),
				isActive: true,
				role: "user",
			};

			const { data: userData, error: userError } = await supabase
				.from("users")
				.insert([userToDbUser(newUser)])
				.select()
				.single();

			if (userError) throw userError;

			return { user: authData.user, profile: dbUserToUser(userData as DbUser) };
		}

		return { user: null, profile: null };
	} catch (error) {
		console.error("サインアップエラー:", error);
		throw error;
	}
};

export const saveGoogleUser = async (
	authUser: AuthUser,
): Promise<User | null> => {
	console.log("saveGoogleUser 関数が呼び出されました", authUser);
	try {
		console.log("Google認証ユーザーの保存を開始:", authUser);

		const userData: Partial<User> = {
			id: authUser.id,
			username:
				authUser.user_metadata.full_name || authUser.email?.split("@")[0] || "",
			email: authUser.email || "",
			fullName: authUser.user_metadata.full_name || "",
			avatarUrl: authUser.user_metadata.avatar_url || "",
			lastLogin: new Date().toISOString(),
		};

		// upsert操作を使用して、既存ユーザーの更新または新規ユーザーの挿入を行う
		const { data, error } = await supabase
			.from("users")
			.upsert(
				{
					...userToDbUser(userData as User),
					created_at: new Date().toISOString(),
					updated_at: new Date().toISOString(),
					is_active: true,
					role: "user",
				},
				{ onConflict: "id", ignoreDuplicates: false },
			)
			.select()
			.single();

		if (error) {
			console.error("ユーザー保存/更新エラー:", error);
			throw error;
		}

		console.log("ユーザー情報を保存/更新しました:", data);
		return dbUserToUser(data as DbUser);
	} catch (error) {
		console.error("Google認証ユーザー保存エラー:", error);
		return null;
	}
};

export const fetchUserProfile = async (
	userId: string,
): Promise<User | null> => {
	const { data, error } = await supabase
		.from("users")
		.select("*")
		.eq("id", userId)
		.single();

	if (error) {
		console.error("ユーザープロフィール取得エラー:", error);
		return null;
	}

	return dbUserToUser(data as DbUser);
};
